/**
 * Reading a sheet in the browser (prototype `csvRows` / `xlsxRows` / `bulkParse`, docs/prototype.html L2640–L2675).
 * The browser only turns the file into rows; what a row means (frequency, day, which clients) is decided by the
 * server on POST /routines/bulk `mode: 'sheet'` (a `dry_run` first for the preview), so the rules live in one place.
 *
 * No library: an .xlsx is a zip, read with the browser's own DecompressionStream; the first sheet is used.
 */
import { spreadsheetCell, type BulkRoutinesBody } from '@amplispot/contracts';

/** One row as the contract takes it (RoutineSheetRow). */
export type SheetRow = Extract<BulkRoutinesBody, { mode: 'sheet' }>['rows'][number];

/**
 * Comma, tab or semicolon separated text → rows of trimmed cells. Quotes are honoured across the whole text, so a
 * quoted cell may hold the separator, a doubled quote (`""`) or a line break (common in a sheet exported to CSV).
 * Blank rows are dropped.
 */
export function csvRows(txt: string): string[][] {
  const d = txt.includes('\t') ? '\t' : txt.includes(';') && !txt.includes(',') ? ';' : ',';
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const endCell = () => {
    row.push(cell.trim());
    cell = '';
  };
  const endRow = () => {
    endCell();
    if (row.some(Boolean)) rows.push(row);
    row = [];
  };
  for (let i = 0; i < txt.length; i++) {
    const ch = txt[i]!;
    if (quoted) {
      if (ch === '"' && txt[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && !cell.trim()) {
      cell = '';
      quoted = true;
    } else if (ch === d) endCell();
    else if (ch === '\n') endRow();
    else if (ch !== '\r') cell += ch;
  }
  endRow();
  return rows;
}

async function zipText(file: Blob, name: string): Promise<string | null> {
  const buf = new Uint8Array(await file.arrayBuffer());
  const dv = new DataView(buf.buffer);
  let e = buf.length - 22;
  while (e >= 0 && dv.getUint32(e, true) !== 0x06054b50) e--;
  if (e < 0) return null;
  const n = dv.getUint16(e + 10, true);
  let off = dv.getUint32(e + 16, true);
  for (let i = 0; i < n; i++) {
    if (dv.getUint32(off, true) !== 0x02014b50) break;
    const method = dv.getUint16(off + 10, true);
    const size = dv.getUint32(off + 20, true);
    const nl = dv.getUint16(off + 28, true);
    const el = dv.getUint16(off + 30, true);
    const cl = dv.getUint16(off + 32, true);
    const lho = dv.getUint32(off + 42, true);
    const nm = new TextDecoder().decode(buf.slice(off + 46, off + 46 + nl));
    if (nm === name) {
      const st = lho + 30 + dv.getUint16(lho + 26, true) + dv.getUint16(lho + 28, true);
      const data = buf.slice(st, st + size);
      if (method === 0) return new TextDecoder().decode(data);
      return new Response(new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'))).text();
    }
    off += 46 + nl + el + cl;
  }
  return null;
}

const NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const NS_REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

/**
 * The zip path of the workbook's first sheet, in tab order: `<sheets>` in xl/workbook.xml names it by `r:id`, and
 * xl/_rels/workbook.xml.rels maps that id to its file. It is not always sheet1.xml (a deleted or reordered sheet).
 */
export function firstSheetPath(workbookXml: string | null, relsXml: string | null): string {
  const fallback = 'xl/worksheets/sheet1.xml';
  if (!workbookXml || !relsXml) return fallback;
  const P = new DOMParser();
  const first = P.parseFromString(workbookXml, 'application/xml').getElementsByTagNameNS(NS, 'sheet')[0];
  const rid = first?.getAttributeNS(NS_REL, 'id') ?? first?.getAttribute('r:id');
  if (!rid) return fallback;
  const rel = Array.from(P.parseFromString(relsXml, 'application/xml').getElementsByTagName('Relationship')).find((r) => r.getAttribute('Id') === rid);
  const target = rel?.getAttribute('Target');
  if (!target) return fallback;
  return target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
}

/** The first worksheet (in tab order) of an .xlsx as rows of trimmed cells. */
export async function xlsxRows(file: Blob): Promise<string[][]> {
  const ss = await zipText(file, 'xl/sharedStrings.xml');
  const path = firstSheetPath(await zipText(file, 'xl/workbook.xml'), await zipText(file, 'xl/_rels/workbook.xml.rels'));
  const sh = await zipText(file, path);
  if (!sh) return [];
  const P = new DOMParser();
  const strs: string[] = [];
  if (ss)
    Array.from(P.parseFromString(ss, 'application/xml').getElementsByTagNameNS(NS, 'si')).forEach((si) =>
      strs.push(Array.from(si.getElementsByTagNameNS(NS, 't')).map((t) => t.textContent ?? '').join('')),
    );
  const col = (r: string) => {
    let n = 0;
    for (const ch of r.replace(/\d/g, '')) n = n * 26 + ch.charCodeAt(0) - 64;
    return n - 1;
  };
  return Array.from(P.parseFromString(sh, 'application/xml').getElementsByTagNameNS(NS, 'row'))
    .map((row) => {
      const out: string[] = [];
      Array.from(row.getElementsByTagNameNS(NS, 'c')).forEach((c) => {
        const t = c.getAttribute('t');
        const v = c.getElementsByTagNameNS(NS, 'v')[0];
        const is = c.getElementsByTagNameNS(NS, 't')[0];
        const val = t === 's' && v ? strs[Number(v.textContent)] : t === 'inlineStr' && is ? is.textContent : v ? v.textContent : '';
        out[col(c.getAttribute('r') ?? 'A')] = (val ?? '').trim();
      });
      return Array.from(out, (x) => x ?? '');
    })
    .filter((r) => r.some(Boolean));
}

/** A chosen file → rows. Throws on a file that cannot be read. */
export async function readSheet(file: File): Promise<string[][]> {
  const rows = file.name.toLowerCase().endsWith('.xlsx') ? await xlsxRows(file) : csvRows(await file.text());
  return rows.filter((r) => r.some((x) => String(x).trim()));
}

/** Whole-cell column names (prototype bulkParse's header words), so a data value never reads as one. */
const HEADER: Record<'t' | 'f' | 'd' | 'c', RegExp> = {
  t: /^(task|tasks|what|action|activity|what does the spoc do)$/,
  f: /^(frequency|how often|every|repeat|repeats|cadence)$/,
  d: /^(day|on|date|when|due day|day of the month)$/,
  c: /^(client|clients|client name|client names|customer|customers)$/,
};
const headerWord = (x: string) => x.trim().toLowerCase().replace(/[\s:*]+$/, '').replace(/\s+/g, ' ');

/**
 * Rows → the contract's Task, Frequency, Day, Clients. The first row is a header only when at least two of its
 * cells are, in full, column names ("Task", "How often", "Clients"…); a data row such as
 * "Send the status, Weekly, Wed, All clients" never is. Without a header the columns are taken in that order.
 */
export function routineSheetRows(rows: string[][]): SheetRow[] {
  if (!rows.length) return [];
  const h = rows[0]!.map(headerWord);
  const find = (k: keyof typeof HEADER) => h.findIndex((x) => HEADER[k].test(x));
  const found = { t: find('t'), f: find('f'), d: find('d'), c: find('c') };
  const hasHeader = Object.values(found).filter((i) => i >= 0).length >= 2;
  const ix = hasHeader ? found : { t: 0, f: 1, d: 2, c: 3 };
  const cell = (r: string[], i: number, max: number) => (i < 0 ? '' : unguard((r[i] ?? '').trim()).slice(0, max));
  return rows
    .slice(hasHeader ? 1 : 0)
    .map((r) => ({ task: cell(r, ix.t, 200), frequency: cell(r, ix.f, 40), day: cell(r, ix.d, 20), clients: cell(r, ix.c, 4000) }))
    .filter((r) => r.task || r.frequency || r.day || r.clients);
}

/**
 * The apostrophe spreadsheetCell puts in front of a cell that starts with = + - @ (sf-7924e87f) is not part of the
 * value: a template downloaded and uploaded again as it is must name the same client, so it comes off here.
 */
const unguard = (v: string) => v.replace(/^'(?=[=+\-@\t\r])/, '');

/** One CSV field, as every CSV download writes it: guarded against formulas (sf-577ff216) and quoted, `"` doubled. */
export const csvField = (v: string): string => `"${spreadsheetCell(v).replace(/"/g, '""')}"`;

/**
 * "Download a template" on Routine → Add from a sheet: example rows from the caller's own clients, never hard-wired
 * names. The clients' names are typed by people (a SPOC may rename one "=HYPERLINK(…)"), so every cell goes through
 * csvField rather than being written raw (sf-7924e87f).
 */
export function routineTemplateCsv(clients: readonly { name: string; segment?: string | null }[]): string {
  const seg = clients[0]?.segment ?? 'BFSI';
  const names = clients.slice(0, 2).map((c) => c.name).join('; ');
  const rows: string[][] = [
    ['Task', 'Frequency', 'Day', 'Clients'],
    ['Send the weekly status to the client', 'Weekly', 'Wed', 'All'],
    ['Review the SMS delivery report', 'Every 2 weeks', 'Mon', seg],
    ['Share the monthly business summary', 'Monthly', '28', 'High priority'],
    ...(names ? [['Check the overnight file arrived', 'Daily', '', names]] : []),
  ];
  return rows.map((r) => r.map(csvField).join(',')).join('\n') + '\n';
}
